// The tablet's navigation: the same five places as the phone's bottom bar
// (frame v2.12, template L68-82), stood on end down the left edge, with the
// red Raise disc at the foot where a thumb holding the tablet's edge lands on
// it. The floating + is a phone control: a tablet has Scan in its header
// already, and a + at the foot of the rail that held Scan again was a second
// door to a thing one tap away.

import { Icon, type IconName } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import { TABS } from '@/lib/tabs';
import type { Tab } from '@/store/slices/ui';

export function SideRail({
  tab,
  onTab,
  onRaise,
  badges = {},
}: {
  /** Null while a job is open: no place is "current" mid-pod. */
  tab: Tab | null;
  onTab: (t: Tab) => void;
  /** The raise kinds list, from the foot of the rail. */
  onRaise: () => void;
  /** The count on each place's badge. Hidden at zero. See `lib/tabs`. */
  badges?: Partial<Record<Tab, number>>;
}) {
  const t = useT();
  return (
    <nav
      aria-label={t.sectionsT}
      style={{
        flex: 'none',
        width: 76,
        background: 'var(--surface)',
        borderRight: '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '14px 0',
      }}
    >
      {TABS.map((item) => {
        const active = tab === item.key;
        const badge = badges[item.key] ?? 0;
        return (
          <button
            key={item.key}
            type="button"
            className="press"
            aria-current={active ? 'page' : undefined}
            onClick={() => onTab(item.key)}
            style={{
              width: 60,
              minHeight: 56,
              borderRadius: 14,
              background: active ? 'var(--ink)' : 'transparent',
              color: active ? 'var(--bg)' : 'var(--sub)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              position: 'relative',
              font: '700 10px Manrope,sans-serif',
            }}
          >
            <Icon name={item.icon} size={20} strokeWidth={2} />
            <span>{t[item.label]}</span>
            {badge > 0 && (
              <span
                aria-label={`${badge} ${t.needsYouT}`}
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  background: 'var(--danger)',
                  color: '#fff',
                  borderRadius: 999,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  display: 'grid',
                  placeItems: 'center',
                  font: '700 9.5px Manrope,sans-serif',
                }}
              >
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </button>
        );
      })}
      <FootButton icon="hazard" label={t.raiseT} onClick={onRaise} />
    </nav>
  );
}

/** The red disc at the foot of the rail: Raise (frame template L77-80). */
function FootButton({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="press"
      onClick={onClick}
      aria-label={label}
      style={{
        marginTop: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        color: 'var(--danger)',
        font: '700 10px Manrope,sans-serif',
      }}
    >
      <span
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: 'var(--danger)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          boxShadow: '0 8px 18px var(--dangerSoft)',
        }}
      >
        <Icon name={icon} size={22} strokeWidth={2.2} />
      </span>
      {label}
    </button>
  );
}
